import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import ConfirmModal from '../../components/commons/confirm-modal/ConfirmModal';

/** Options for a single confirmation prompt. Mirrors window.confirm but styled. */
export interface ConfirmOptions {
    title: string;
    message?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Render the confirm button in the danger style (deletes, revokes…). */
    danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

/**
 * App-wide replacement for window.confirm(). Renders a single styled
 * ConfirmModal and exposes an async `confirm()` that resolves to true/false:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, message, danger: true }))) return;
 */
export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [opts, setOpts] = useState<ConfirmOptions | null>(null);
    const resolver = useRef<((value: boolean) => void) | null>(null);

    const confirm = useCallback<ConfirmFn>((options) => {
        setOpts(options);
        return new Promise<boolean>((resolve) => {
            resolver.current = resolve;
        });
    }, []);

    const finish = useCallback((value: boolean) => {
        setOpts(null);
        resolver.current?.(value);
        resolver.current = null;
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <ConfirmModal
                open={!!opts}
                title={opts?.title ?? ''}
                message={opts?.message}
                confirmLabel={opts?.confirmLabel}
                cancelLabel={opts?.cancelLabel}
                danger={opts?.danger}
                onConfirm={() => finish(true)}
                onCancel={() => finish(false)}
            />
        </ConfirmContext.Provider>
    );
};

export const useConfirm = (): ConfirmFn => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return ctx;
};
